import { FC, useEffect, useState } from 'react';

import AddressFinder, { addressFinderSchema } from '@eneco/components/AddressFinder/AddressFinder';
import RichText from '@eneco/components/RichText/RichText';
import useDC from '@eneco/dc/useDC';
import { Header, Layout, Overview } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Button, InputText, NotificationBox, Stack, Text } from '@eneco/sparky';
import {
  Customers_Address_AddressModel,
  DC_Customers_Client_Models_CustomerDiscontinueIntakeAddressResponseModel,
} from '@monorepo-types/dc';
import { usePlaceholderContent } from '@sitecore/common';
import { DeceasedFlowDeceasedPersonSearchStepRendering } from '@sitecore/types/DeceasedFlowDeceasedPersonSearchStep';
import { useMachine } from '@xstate/react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextDeceased } from '../../context';
import { searchMachine } from '../machines/searchMachine';

interface FormData {
  personSearchCustomerId: number;
  postalCode: string;
  houseNumber: number;
  houseNumberSuffix?: string;
  customerId?: number;
}

export const PersonSearch: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextDeceased>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const [searchState, sendToSearchMachine] = useMachine(searchMachine);
  const { label, businessUnit } = useDC();
  const [personSearchCustomerId, setPersonSearchCustomerId] = useState<number | null>(null);
  const [isHouseNumberSuffixRequired, setIsHouseNumberSuffixRequired] = useState(false);
  const [addressFinderData, setAddressFinderData] = useState<Customers_Address_AddressModel>();
  const [address, setAddress] =
    useState<DC_Customers_Client_Models_CustomerDiscontinueIntakeAddressResponseModel | null>(null);
  const [showError, setShowError] = useState(false);
  const { DeceasedFlowDeceasedPersonSearchStep: { fields: textualData } = {} } = usePlaceholderContent<{
    DeceasedFlowDeceasedPersonSearchStep: DeceasedFlowDeceasedPersonSearchStepRendering;
  }>();
  const CUSTOMERNUMBER_FALLBACK = 'personSearchCustomerId';

  const customerId = useFlowSelector(state => state.context.customerId);
  const deceasedCustomer = useFlowSelector(state => state.context.deceasedCustomer);

  const formSchema = yup.object({
    personSearchCustomerId: yup
      .string()
      .required(textualData?.contractDetails.customerNumberFormField.value.requiredMessage),
    ...addressFinderSchema(isHouseNumberSuffixRequired).fields,
  });

  const formMethods = useForm<FormData>({
    mode: 'onSubmit',
    resolver: typedYupResolver<FormData>(formSchema),
    defaultValues: {
      personSearchCustomerId: customerId ?? undefined,
      houseNumber: deceasedCustomer?.address?.houseNumber ?? undefined,
      postalCode: deceasedCustomer?.address?.postalCode ?? '',
      houseNumberSuffix: deceasedCustomer?.address?.houseNumberSuffix?.toString() ?? '',
    },
  });

  const searchPerson = (formData: FormData) => {
    if (searchState.matches('ERROR')) {
      sendToSearchMachine({ type: 'RESET' });
    }
    setPersonSearchCustomerId(formData.personSearchCustomerId);
    sendToSearchMachine({
      type: 'GET_CUSTOMER',
      config: {
        businessUnit,
        label,
      },
      values: {
        customerId: Number(formData.personSearchCustomerId),
        postalCode: addressFinderData?.postalCode ?? '',
        houseNumber: Number(addressFinderData?.houseNumber),
        houseNumberSuffix: addressFinderData?.houseNumberSuffix ?? '',
        street: addressFinderData?.street ?? '',
      },
    });
  };

  useEffect(() => {
    if (searchState.matches('SUCCESS')) {
      setAddress(searchState.context.deceasedCustomerInformation?.address || null);
      setShowError(false);
    } else if (searchState.matches('ERROR')) {
      setShowError(true);
    }
  }, [searchState, setAddress]);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = formMethods;

  const buttonClicked = handleSubmit(formData => {
    setShowError(false);
    searchPerson(formData);

    if (!showError && address) {
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          deceasedCustomer: searchState.context.deceasedCustomerInformation,
          customerId: personSearchCustomerId,
        },
      });
    }
  });

  const {
    hint: customerNumberFormFieldHint,
    label: customerNumberFormFieldLabel,
    placeholder: customerNumberFormFieldPlaceholder,
  } = textualData?.contractDetails?.customerNumberFormField?.value ?? {};

  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <FormProvider {...formMethods}>
        <Box paddingY={{ initial: '8', md: '12', xl: '16' }} paddingX={{ initial: '6', md: '10', lg: '0' }}>
          <Stack gap="6">
            <Header>
              <Header.Title>{textualData?.content?.title?.value}</Header.Title>
              <Header.Text>{textualData?.content?.text?.value}</Header.Text>
            </Header>

            <Stack.Item>
              <Stack.Item>
                <Box paddingBottom="6">
                  <AddressFinder
                    customLabels={{
                      houseNumber: textualData?.address?.houseNumberFormField,
                      postalCode: textualData?.address?.postalCodeFormField,
                      houseNumberSuffix: textualData?.address?.houseNumberSuffixFormField,
                    }}
                    isSuffixRequired={isHouseNumberSuffixRequired}
                    setIsSuffixRequired={setIsHouseNumberSuffixRequired}
                    setAddressFinderData={setAddressFinderData}
                  />
                </Box>
                <Box>
                  <InputText
                    hint={customerNumberFormFieldHint}
                    label={customerNumberFormFieldLabel || ''}
                    placeholder={customerNumberFormFieldPlaceholder}
                    error={errors.personSearchCustomerId?.message}
                    {...register(CUSTOMERNUMBER_FALLBACK)}
                  />
                </Box>
              </Stack.Item>
            </Stack.Item>

            {showError && (
              <Stack.Item>
                <NotificationBox
                  isAlert
                  text={<RichText html={textualData?.notifications?.dataNotFoundNotification?.value.content} />}
                  title={textualData?.notifications?.dataNotFoundNotification?.value.title}
                  variant="error"
                />
              </Stack.Item>
            )}
            {address && (
              <>
                <NotificationBox
                  isAlert
                  text={<RichText html={textualData?.notifications?.dataFoundNotification?.value.content} />}
                  title={textualData?.notifications?.dataFoundNotification?.value.title}
                  variant="success"
                />
                <Stack.Item>
                  <Overview>
                    <Overview.Header>{textualData?.customerSearchResult?.cardTitle?.value}</Overview.Header>
                    <Overview.Details
                      type="profile"
                      title={textualData?.customerSearchResult?.personalDetailsLabel?.value ?? ''}>
                      <Text as="p">
                        {`${textualData?.customerSearchResult?.streetLabel?.value}: ${address?.street ?? ''}`}{' '}
                        {address?.houseNumber ?? ''} {address?.houseNumberSuffix ?? ''}
                      </Text>
                      <Text as="p">{`${textualData?.customerSearchResult?.cityLabel?.value}: ${address?.city}`}</Text>
                    </Overview.Details>
                  </Overview>
                </Stack.Item>
                <Stack.Item>
                  <Text>{textualData?.customerSearchResult?.dataDisclaimerText?.value}</Text>
                </Stack.Item>
              </>
            )}

            <Stack.Item>
              <Button onClick={buttonClicked}>{textualData?.content?.nextStepText?.value}</Button>
            </Stack.Item>
          </Stack>
        </Box>
      </FormProvider>
    </Layout.Content>
  );
};
